import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeDollarSign,
  Ban,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleCheck,
  RefreshCcw,
  Search,
  ShieldCheck,
  Trash2,
  User,
  Users,
} from "lucide-react";
import API from "../../services/api";
import CustomSelect from "../../components/CustomSelect";
import PageModal from "../../components/PageModal";
import { useToast } from "../../components/Toast";
import { loadAdminUsersData } from "./adminShared";
import { formatAppDateTime } from "../../utils/dateFormat";
import "./AdminShared.css";
import "./AdminUsers.css";
import "./AdminCommissions.css";

function formatCurrency(value) {
  const amount = Number(value);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
}

const payoutFilterOptions = [
  { value: "all", label: "All" },
  { value: "unpaid", label: "Unpaid" },
  { value: "paid", label: "Paid" },
];

const payoutStatusOptions = [
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
];

const roleOptions = [
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];

const activationFilterOptions = [
  { value: "all", label: "All users" },
  { value: "active", label: "Active only" },
  { value: "inactive", label: "Inactive only" },
];

const roleFilterOptions = [
  { value: "all", label: "All roles" },
  { value: "admin", label: "Admins" },
  { value: "user", label: "Users" },
];

const pageSizeOptions = [
  { value: "10", label: "10" },
  { value: "50", label: "50" },
  { value: "100", label: "100" },
];

export default function AdminUsers() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [activationFilter, setActivationFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortKey, setSortKey] = useState("role");
  const [sortDirection, setSortDirection] = useState("asc");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [actionBusy, setActionBusy] = useState(null);
  const [confirmUser, setConfirmUser] = useState(null);

  const loadUsers = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const next = await loadAdminUsersData();
      setUsers(next.users);
      setError(
        next.errors.length ? `Some user data could not be loaded: ${next.errors.join(", ")}.` : "",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const processedUsers = useMemo(() => {
    const term = userFilter.trim().toLowerCase();

    const filtered = users.filter((user) => {
      const matchesSearch =
        !term ||
        String(user.email || "")
          .toLowerCase()
          .includes(term) ||
        String(user.name || "")
          .toLowerCase()
          .includes(term);

      const matchesActivation =
        activationFilter === "all" ||
        (activationFilter === "active" && user.isActive) ||
        (activationFilter === "inactive" && !user.isActive);

      const matchesRole = roleFilter === "all" || user.role === roleFilter;

      return matchesSearch && matchesActivation && matchesRole;
    });

    return [...filtered].sort((left, right) => {
      const getComparable = (user) => {
        switch (sortKey) {
          case "role":
            return user.role === "admin" ? 0 : 1;
          case "status":
            return user.isActive ? "active" : "inactive";
          case "sets":
            return Number(user.accountSetsCount || 0);
          case "terminals":
            return Number(user.mt4Count || 0) + Number(user.mt5Count || 0);
          default:
            return String(user.name || user.email || "").toLowerCase();
        }
      };

      const a = getComparable(left);
      const b = getComparable(right);
      if (a < b) return sortDirection === "asc" ? -1 : 1;
      if (a > b) return sortDirection === "asc" ? 1 : -1;
      const leftName = String(left.name || left.email || "").toLowerCase();
      const rightName = String(right.name || right.email || "").toLowerCase();
      if (leftName < rightName) return -1;
      if (leftName > rightName) return 1;
      return 0;
    });
  }, [activationFilter, roleFilter, sortDirection, sortKey, userFilter, users]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activationFilter, roleFilter, pageSize, sortDirection, sortKey, userFilter]);

  const totalPages = Math.max(1, Math.ceil(processedUsers.length / pageSize));

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return processedUsers.slice(start, start + pageSize);
  }, [currentPage, pageSize, processedUsers]);

  const handleSort = (key) => {
    setSortDirection((currentDirection) => {
      if (sortKey === key) {
        return currentDirection === "asc" ? "desc" : "asc";
      }
      return "desc";
    });
    setSortKey(key);
  };

  const handleRoleChange = async (userId, role) => {
    setActionBusy({ type: "role", id: userId });
    try {
      await API.put(`/admin/users/${userId}`, { role });
      toast.success("User role updated.");
      await loadUsers(true);
    } catch (err) {
      toast.error(err.response?.data?.msg || err.message);
    } finally {
      setActionBusy(null);
    }
  };

  const handleActivationToggle = async (user) => {
    const nextIsActive = !user.isActive;
    setActionBusy({ type: "activation", id: user.id });
    try {
      await API.put(`/admin/users/${user.id}`, { isActive: nextIsActive });
      toast.success(nextIsActive ? "User activated." : "User deactivated.");
      await loadUsers(true);
    } catch (err) {
      toast.error(err.response?.data?.msg || err.message);
    } finally {
      setActionBusy(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!confirmUser) return;
    setActionBusy({ type: "delete-user", id: confirmUser.id });
    try {
      await API.delete(`/admin/users/${confirmUser.id}`);
      toast.success("User deleted.");
      setConfirmUser(null);
      await loadUsers(true);
    } catch (err) {
      toast.error(err.response?.data?.msg || err.message);
    } finally {
      setActionBusy(null);
    }
  };

  const renderHeaderSort = (label, key) => {
    const active = sortKey === key;
    return (
      <button
        type="button"
        className={`admin-users-sort${active ? " admin-users-sort-active" : ""}`}
        onClick={() => handleSort(key)}
      >
        <span>{label}</span>
        {active ? (
          <ChevronDown
            className={`admin-users-sort-icon${sortDirection === "desc" ? " admin-users-sort-icon-desc" : ""}`}
          />
        ) : (
          <ChevronsUpDown className="admin-users-sort-icon" />
        )}
      </button>
    );
  };

  const rowStart = processedUsers.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rowEnd = Math.min(currentPage * pageSize, processedUsers.length);
  const skeletonRows = Array.from({ length: 6 });

  const [commissions, setCommissions] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(true);
  const [commissionsRefreshing, setCommissionsRefreshing] = useState(false);
  const [commissionSearch, setCommissionSearch] = useState("");
  const [commissionPayoutFilter, setCommissionPayoutFilter] = useState("all");
  const [commissionSortKey, setCommissionSortKey] = useState("createdAt");
  const [commissionSortDir, setCommissionSortDir] = useState("desc");
  const [commissionPageSize, setCommissionPageSize] = useState(10);
  const [commissionPage, setCommissionPage] = useState(1);
  const [payoutModal, setPayoutModal] = useState(null);
  const [payoutBusy, setPayoutBusy] = useState(false);

  const loadCommissions = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setCommissionsRefreshing(true);
      else setCommissionsLoading(true);
      try {
        const res = await API.get("/admin/referrals/commissions");
        const items = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.commissions)
            ? res.data.commissions
            : [];
        setCommissions(items);
      } catch {
        toast.error("Failed to load commissions.");
      } finally {
        setCommissionsLoading(false);
        setCommissionsRefreshing(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    loadCommissions();
  }, [loadCommissions]);

  const commissionRows = useMemo(
    () =>
      commissions.map((c, i) => {
        const sponsor = c?.sponsor || {};
        const ref = c?.referredUser || {};
        const paidBy = c?.paidByUser || null;
        return {
          id: String(c?.id || c?._id || i),
          sponsorName: sponsor.displayName || sponsor.name || sponsor.email || "—",
          sponsorEmail: sponsor.email || "",
          refName: ref.displayName || ref.name || ref.email || "—",
          refEmail: ref.email || "",
          planName: c?.planName || "—",
          subscriptionAmount: c?.subscriptionAmount ?? null,
          commissionRate: c?.commissionRate ?? null,
          commissionAmount: c?.commissionAmount ?? null,
          payoutStatus: c?.payoutStatus || "unpaid",
          paidAt: c?.paidAt || null,
          paidByName: paidBy ? paidBy.displayName || paidBy.name || paidBy.email || "—" : null,
          payoutNote: c?.payoutNote || null,
          payoutReference: c?.payoutReference || null,
          createdAt: c?.createdAt || "",
          searchText: [
            sponsor.email,
            sponsor.name,
            ref.email,
            ref.name,
            c?.planName,
            c?.payoutStatus,
          ]
            .map((v) => String(v || "").toLowerCase())
            .join(" "),
        };
      }),
    [commissions],
  );

  const processedCommissions = useMemo(() => {
    const term = commissionSearch.trim().toLowerCase();
    const filtered = commissionRows.filter((r) => {
      if (commissionPayoutFilter !== "all" && r.payoutStatus !== commissionPayoutFilter)
        return false;
      if (term && !r.searchText.includes(term)) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      const v = (r) => {
        switch (commissionSortKey) {
          case "sponsorName":
            return String(r.sponsorName).toLowerCase();
          case "refName":
            return String(r.refName).toLowerCase();
          case "planName":
            return String(r.planName).toLowerCase();
          case "commissionAmount":
            return Number(r.commissionAmount ?? 0);
          case "payoutStatus":
            return r.payoutStatus === "paid" ? 0 : 1;
          default:
            return new Date(r.createdAt).getTime() || 0;
        }
      };
      const av = v(a),
        bv = v(b);
      if (av < bv) return commissionSortDir === "asc" ? -1 : 1;
      if (av > bv) return commissionSortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [
    commissionRows,
    commissionSearch,
    commissionPayoutFilter,
    commissionSortKey,
    commissionSortDir,
  ]);

  useEffect(() => {
    setCommissionPage(1);
  }, [
    commissionSearch,
    commissionPayoutFilter,
    commissionSortKey,
    commissionSortDir,
    commissionPageSize,
  ]);

  const commissionTotalPages = Math.max(
    1,
    Math.ceil(processedCommissions.length / commissionPageSize),
  );
  const paginatedCommissions = useMemo(() => {
    const start = (commissionPage - 1) * commissionPageSize;
    return processedCommissions.slice(start, start + commissionPageSize);
  }, [commissionPage, commissionPageSize, processedCommissions]);

  const cRowStart =
    processedCommissions.length === 0 ? 0 : (commissionPage - 1) * commissionPageSize + 1;
  const cRowEnd = Math.min(commissionPage * commissionPageSize, processedCommissions.length);

  const handleCommissionSort = (key) => {
    setCommissionSortDir((cur) =>
      commissionSortKey === key ? (cur === "asc" ? "desc" : "asc") : "desc",
    );
    setCommissionSortKey(key);
  };

  const renderCommissionSort = (label, key) => {
    const active = commissionSortKey === key;
    return (
      <button
        type="button"
        className={`admin-users-sort${active ? " admin-users-sort-active" : ""}`}
        onClick={() => handleCommissionSort(key)}
      >
        <span>{label}</span>
        {active ? (
          <ChevronDown
            className={`admin-users-sort-icon${commissionSortDir === "desc" ? " admin-users-sort-icon-desc" : ""}`}
          />
        ) : (
          <ChevronsUpDown className="admin-users-sort-icon" />
        )}
      </button>
    );
  };

  const openPayoutModal = (row) => {
    setPayoutModal({
      row,
      payoutStatus: row.payoutStatus || "paid",
      note: row.payoutNote || "",
      reference: row.payoutReference || "",
    });
  };

  const handlePayoutUpdate = async () => {
    if (!payoutModal) return;
    setPayoutBusy(true);
    try {
      const body = {
        payoutStatus: payoutModal.payoutStatus,
        note: payoutModal.note.trim() || undefined,
        reference: payoutModal.reference.trim() || undefined,
      };
      const res = await API.patch(
        `/admin/referrals/commissions/${payoutModal.row.id}/payout`,
        body,
      );
      const updated = res?.data?.commission;
      if (updated) {
        setCommissions((prev) =>
          prev.map((c) => (String(c?.id || c?._id) === payoutModal.row.id ? updated : c)),
        );
      }
      toast.success(
        payoutModal.payoutStatus === "paid"
          ? "Commission marked as paid."
          : "Commission marked as unpaid.",
      );
      setPayoutModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to update payout.");
      setPayoutModal(null);
    } finally {
      setPayoutBusy(false);
    }
  };

  return (
    <>
      <div className="admin-page admin-users-page">
        <div className="admin-users-header">
          <div>
            <h1>
              <Users className="admin-users-header-icon" />
              <span>User Directory</span>
            </h1>
            <p>
              Manage access, update roles, activation state, and account access without leaving the
              admin workspace.
            </p>
          </div>
          <div className="admin-page-actions">
            <button
              className="admin-btn admin-btn-primary admin-users-refresh-btn"
              onClick={() => loadUsers(true)}
              disabled={loading || refreshing}
            >
              <RefreshCcw size={15} />
              {refreshing ? "Refreshing..." : "Refresh Users"}
            </button>
          </div>
        </div>

        {error && <div className="admin-inline-alert error">{error}</div>}

        <div className="admin-users-summary-grid">
          <div className="admin-users-summary-card">
            <div className="admin-users-summary-icon">
              <Users size={20} />
            </div>
            <div className="admin-users-summary-copy">
              <span>Total Users</span>
              <strong>{users.length}</strong>
              <small>All accessible user accounts</small>
            </div>
          </div>
          <div className="admin-users-summary-card">
            <div className="admin-users-summary-icon">
              <ShieldCheck size={20} />
            </div>
            <div className="admin-users-summary-copy">
              <span>Total Admins</span>
              <strong>{users.filter((user) => user.role === "admin").length}</strong>
              <small>Current elevated accounts</small>
            </div>
          </div>
          <div className="admin-users-summary-card">
            <div className="admin-users-summary-icon">
              <CircleCheck size={20} />
            </div>
            <div className="admin-users-summary-copy">
              <span>Active Users</span>
              <strong>{users.filter((user) => user.isActive).length}</strong>
              <small>Currently enabled accounts</small>
            </div>
          </div>
          <div className="admin-users-summary-card">
            <div className="admin-users-summary-icon">
              <Ban size={20} />
            </div>
            <div className="admin-users-summary-copy">
              <span>Inactive Users</span>
              <strong>{users.filter((user) => !user.isActive).length}</strong>
              <small>Currently disabled accounts</small>
            </div>
          </div>
        </div>

        <section className="admin-card admin-users-card">
          <div className="admin-card-heading">
            <div>
              <div className="admin-card-title">
                <User />
                <span>User Access</span>
              </div>
              <p>
                Search, filter, sort from the table header, and manage activation or role updates
                inline.
              </p>
            </div>
          </div>

          <div className="admin-users-toolbar">
            <div className="admin-field-group admin-users-toolbar-search">
              <label className="admin-label">
                <Search size={13} />
                Search
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="Search by name or email"
                value={userFilter}
                onChange={(event) => setUserFilter(event.target.value)}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Status</label>
              <CustomSelect
                value={activationFilter}
                onChange={setActivationFilter}
                options={activationFilterOptions}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Role</label>
              <CustomSelect
                value={roleFilter}
                onChange={setRoleFilter}
                options={roleFilterOptions}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Count</label>
              <CustomSelect
                value={String(pageSize)}
                onChange={(value) => setPageSize(Number(value))}
                options={pageSizeOptions}
              />
            </div>
          </div>

          <>
            <div className="admin-data-table-wrap admin-users-table-wrap">
              <table className="admin-data-table admin-users-table">
                <thead>
                  <tr>
                    <th>S. No.</th>
                    <th>{renderHeaderSort("User", "name")}</th>
                    <th>{renderHeaderSort("Role", "role")}</th>
                    <th>{renderHeaderSort("Status", "status")}</th>
                    <th>{renderHeaderSort("Sets", "sets")}</th>
                    <th>{renderHeaderSort("MT4 / MT5", "terminals")}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    skeletonRows.map((_, index) => (
                      <tr key={`users-skeleton-${index}`} className="admin-table-skeleton-row">
                        <td>
                          <span className="admin-skeleton-line admin-skeleton-line-sm" />
                        </td>
                        <td>
                          <span className="admin-skeleton-line admin-skeleton-line-lg" />
                        </td>
                        <td>
                          <span className="admin-skeleton-line admin-skeleton-line-md" />
                        </td>
                        <td>
                          <span className="admin-skeleton-line admin-skeleton-line-sm" />
                        </td>
                        <td>
                          <span className="admin-skeleton-line admin-skeleton-line-xs" />
                        </td>
                        <td>
                          <span className="admin-skeleton-line admin-skeleton-line-xs" />
                        </td>
                        <td>
                          <div className="admin-table-skeleton-actions">
                            <span className="admin-skeleton-line admin-skeleton-line-btn" />
                            <span className="admin-skeleton-line admin-skeleton-line-btn" />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user, index) => (
                      <tr key={user.id}>
                        <td>{rowStart + index}</td>
                        <td>
                          <div className="admin-user-meta">
                            <span className="admin-user-primary">{user.name || user.email}</span>
                          </div>
                        </td>
                        <td>
                          <div className="admin-users-role-cell">
                            <CustomSelect
                              value={user.role}
                              onChange={(value) => handleRoleChange(user.id, value)}
                              options={roleOptions}
                              disabled={actionBusy?.type === "role" && actionBusy?.id === user.id}
                              className="admin-role-select"
                            />
                          </div>
                        </td>
                        <td className="admin-users-status-cell">
                          <span
                            className={`admin-badge admin-users-status-badge ${user.isActive ? "admin-badge-success" : "admin-badge-danger"}`}
                          >
                            {user.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>{user.accountSetsCount ?? 0}</td>
                        <td>
                          {user.mt4Count ?? 0} / {user.mt5Count ?? 0}
                        </td>
                        <td>
                          <div className="admin-table-actions">
                            <button
                              className={`admin-btn ${user.isActive ? "admin-btn-danger" : "admin-btn-success"}`}
                              onClick={() => handleActivationToggle(user)}
                              disabled={actionBusy?.id === user.id}
                            >
                              {user.isActive ? <Ban size={15} /> : <CircleCheck size={15} />}
                              {actionBusy?.type === "activation" && actionBusy?.id === user.id
                                ? user.isActive
                                  ? "Deactivating..."
                                  : "Activating..."
                                : user.isActive
                                  ? "Deactivate"
                                  : "Activate"}
                            </button>
                            <button
                              className="admin-btn admin-btn-danger"
                              onClick={() => setConfirmUser(user)}
                              disabled={actionBusy?.id === user.id}
                            >
                              <Trash2 size={15} />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="admin-users-empty-table-row">
                      <td colSpan={7} className="admin-users-empty-table-cell">
                        <div className="admin-users-empty-table-state">No users found.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {!loading && processedUsers.length > 0 && (
              <div className="admin-table-footer admin-users-table-footer">
                <span className="admin-table-summary">
                  Showing {rowStart}-{rowEnd} of {processedUsers.length}
                </span>
                <div className="admin-table-pagination">
                  <button
                    className="admin-btn admin-btn-ghost"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={15} />
                    Prev
                  </button>
                  <span className="admin-users-pagecount">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    className="admin-btn admin-btn-ghost"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </>
        </section>

        <section className="admin-card admin-users-card" style={{ marginTop: "1rem" }}>
          <div className="admin-card-heading">
            <div>
              <div className="admin-card-title">
                <BadgeDollarSign />
                <span>Commissions</span>
              </div>
              <p>View all referral commissions and manually mark payouts as paid.</p>
            </div>
            <div className="admin-page-actions">
              <button
                className="admin-btn admin-btn-primary"
                onClick={() => loadCommissions(true)}
                disabled={commissionsLoading || commissionsRefreshing}
              >
                <RefreshCcw size={15} />
                {commissionsRefreshing ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          <div className="admin-users-toolbar admin-commissions-inner-toolbar">
            <div className="admin-field-group admin-users-toolbar-search">
              <label className="admin-label">
                <Search size={13} />
                Search
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="Search by sponsor, referred user, or plan"
                value={commissionSearch}
                onChange={(e) => setCommissionSearch(e.target.value)}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Status</label>
              <CustomSelect
                value={commissionPayoutFilter}
                onChange={setCommissionPayoutFilter}
                options={payoutFilterOptions}
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Count</label>
              <CustomSelect
                value={String(commissionPageSize)}
                onChange={(v) => setCommissionPageSize(Number(v))}
                options={pageSizeOptions}
              />
            </div>
          </div>

          <div className="admin-data-table-wrap admin-users-table-wrap">
            <table className="admin-data-table admin-users-table">
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>{renderCommissionSort("Sponsor", "sponsorName")}</th>
                  <th>{renderCommissionSort("Referred User", "refName")}</th>
                  <th>{renderCommissionSort("Plan", "planName")}</th>
                  <th>{renderCommissionSort("Commission", "commissionAmount")}</th>
                  <th>{renderCommissionSort("Payout", "payoutStatus")}</th>
                  <th>Paid At / By</th>
                  <th>{renderCommissionSort("Date", "createdAt")}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {commissionsLoading ? (
                  skeletonRows.map((_, i) => (
                    <tr key={`c-sk-${i}`} className="admin-table-skeleton-row">
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-xs" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <div className="admin-table-skeleton-actions">
                          <span className="admin-skeleton-line admin-skeleton-line-btn" />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : paginatedCommissions.length > 0 ? (
                  paginatedCommissions.map((row, i) => (
                    <tr key={row.id}>
                      <td>{cRowStart + i}</td>
                      <td>
                        <div className="admin-commissions-user-meta">
                          <span className="admin-user-primary">{row.sponsorName}</span>
                          {row.sponsorEmail ? (
                            <span className="admin-commissions-user-secondary">
                              {row.sponsorEmail}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="admin-commissions-user-meta">
                          <span className="admin-user-primary">{row.refName}</span>
                          {row.refEmail ? (
                            <span className="admin-commissions-user-secondary">{row.refEmail}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="admin-commissions-user-meta">
                          <span className="admin-user-primary">{row.planName}</span>
                          {row.subscriptionAmount !== null ? (
                            <span className="admin-commissions-user-secondary">
                              {formatCurrency(row.subscriptionAmount)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="admin-commissions-user-meta">
                          <span className="admin-user-primary">
                            {row.commissionAmount !== null
                              ? formatCurrency(row.commissionAmount)
                              : "—"}
                          </span>
                          {row.commissionRate !== null ? (
                            <span className="admin-commissions-user-secondary">
                              {Number(row.commissionRate).toFixed(2)}%
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`admin-badge admin-commissions-payout-badge ${row.payoutStatus === "paid" ? "admin-badge-success" : "admin-badge-warning"}`}
                        >
                          {row.payoutStatus === "paid" ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                      <td>
                        {row.paidAt ? (
                          <div className="admin-commissions-user-meta">
                            <span className="admin-user-primary">
                              {formatAppDateTime(row.paidAt)}
                            </span>
                            {row.paidByName ? (
                              <span className="admin-commissions-user-secondary">
                                by {row.paidByName}
                              </span>
                            ) : null}
                            {row.payoutNote ? (
                              <span className="admin-commissions-user-secondary">
                                {row.payoutNote}
                              </span>
                            ) : null}
                            {row.payoutReference ? (
                              <span className="admin-commissions-user-secondary">
                                Ref: {row.payoutReference}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span style={{ color: "var(--admin-muted)" }}>—</span>
                        )}
                      </td>
                      <td>{formatAppDateTime(row.createdAt)}</td>
                      <td>
                        <div className="admin-table-actions">
                          {row.payoutStatus === "paid" ? (
                            <button
                              className="admin-btn admin-btn-ghost"
                              onClick={() => openPayoutModal(row)}
                            >
                              Edit
                            </button>
                          ) : (
                            <button
                              className="admin-btn admin-btn-primary"
                              onClick={() => openPayoutModal(row)}
                            >
                              Mark Paid
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="admin-users-empty-table-row">
                    <td colSpan={9} className="admin-users-empty-table-cell">
                      <div className="admin-users-empty-table-state">
                        {commissionRows.length > 0
                          ? "No commissions match your filters."
                          : "No commissions found."}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!commissionsLoading && processedCommissions.length > 0 && (
            <div className="admin-table-footer admin-users-table-footer">
              <span className="admin-table-summary">
                Showing {cRowStart}-{cRowEnd} of {processedCommissions.length}
              </span>
              <div className="admin-table-pagination">
                <button
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setCommissionPage((p) => Math.max(1, p - 1))}
                  disabled={commissionPage === 1}
                >
                  <ChevronLeft size={15} />
                  Prev
                </button>
                <span className="admin-users-pagecount">
                  Page {commissionPage} / {commissionTotalPages}
                </span>
                <button
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setCommissionPage((p) => Math.min(commissionTotalPages, p + 1))}
                  disabled={commissionPage === commissionTotalPages}
                >
                  Next
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <PageModal
        open={Boolean(confirmUser)}
        onClose={() => setConfirmUser(null)}
        onConfirm={handleDeleteUser}
        title="Delete this user account?"
        description={`This will permanently remove ${confirmUser?.email || "this user"} from the platform.`}
        confirmLabel={actionBusy?.type === "delete-user" ? "Deleting..." : "Delete user"}
        loading={actionBusy?.type === "delete-user"}
        tone="danger"
      />

      <PageModal
        open={Boolean(payoutModal)}
        onClose={() => !payoutBusy && setPayoutModal(null)}
        onConfirm={handlePayoutUpdate}
        title={
          payoutModal?.row?.payoutStatus === "paid"
            ? "Edit Payout Details"
            : "Mark Commission as Paid"
        }
        description={`Commission for ${payoutModal?.row?.refName || "this referral"} — ${payoutModal?.row?.planName || ""}`}
        confirmLabel={payoutBusy ? "Saving..." : "Save"}
        cancelLabel="Cancel"
        tone="default"
        icon="info"
        loading={payoutBusy}
      >
        <div className="admin-commissions-modal-fields">
          <div className="admin-field-group">
            <label className="admin-label">Payout Status</label>
            <CustomSelect
              value={payoutModal?.payoutStatus || "paid"}
              onChange={(v) =>
                setPayoutModal((p) =>
                  v === "unpaid"
                    ? { ...p, payoutStatus: v, note: "", reference: "" }
                    : { ...p, payoutStatus: v },
                )
              }
              options={payoutStatusOptions}
              disabled={payoutBusy}
            />
          </div>
          <div className="admin-field-group">
            <label className="admin-label">Note (optional)</label>
            <input
              type="text"
              className="admin-input"
              placeholder="e.g. Paid via bank transfer"
              value={payoutModal?.note || ""}
              onChange={(e) => setPayoutModal((p) => ({ ...p, note: e.target.value }))}
              disabled={payoutBusy || payoutModal?.payoutStatus === "unpaid"}
            />
          </div>
          <div className="admin-field-group">
            <label className="admin-label">Reference (optional)</label>
            <input
              type="text"
              className="admin-input"
              placeholder="e.g. TXN-123"
              value={payoutModal?.reference || ""}
              onChange={(e) => setPayoutModal((p) => ({ ...p, reference: e.target.value }))}
              disabled={payoutBusy || payoutModal?.payoutStatus === "unpaid"}
            />
          </div>
        </div>
      </PageModal>
    </>
  );
}
